import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { Box, Typography, Fade, LinearProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AeDriveLogo from '../../../assets/images/AeDriveLogo.png';

/**
 * Generic Loader Component
 * A beautiful and simple loader with subtle animations for use while pages load data
 * 
 * @param {string} message - Optional message to display below the loader
 * @param {array} sequentialMessages - Array of messages to cycle through sequentially
 * @param {number} messageDelay - Delay between message changes in ms (default: 2000)
 * @param {boolean} fullScreen - Whether to display as full screen overlay (default: true)
 */
const Loader = ({ 
  message = 'Loading...', 
  sequentialMessages = null,
  messageDelay = 2000,
  fullScreen = true 
}) => {
  const theme = useTheme();
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [displayMessage, setDisplayMessage] = useState(message);
  const [progress, setProgress] = useState(10);

  // Handle sequential messages
  useEffect(() => {
    if (!sequentialMessages || sequentialMessages.length === 0) {
      setDisplayMessage(message);
      return;
    }

    const interval = setInterval(() => {
      setCurrentMessageIndex((prevIndex) => {
        const nextIndex = (prevIndex + 1) % sequentialMessages.length;
        setDisplayMessage(sequentialMessages[nextIndex]);
        return nextIndex;
      });
    }, messageDelay);

    // Set initial message
    setDisplayMessage(sequentialMessages[0]);

    return () => clearInterval(interval);
  }, [sequentialMessages, messageDelay, message]);

  // Animate progress bar
  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((prevProgress) => (prevProgress >= 90 ? 10 : prevProgress + 10));
    }, 800);
    return () => {
      clearInterval(timer);
    };
  }, []);

  const loaderContent = (
    <Fade in={true} timeout={600}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 3,
        }}
      >
        {/* Logo with smooth pulse animation */}
        <Fade in={true} timeout={800}>
          <Box
            component="img"
            src={AeDriveLogo}
            alt="AE-Drive Logo"
            sx={{
              width: { xs: 120, sm: 150 },
              height: 'auto',
              animation: 'logoPulse 2s ease-in-out infinite',
              '@keyframes logoPulse': {
                '0%, 100%': {
                  opacity: 1,
                  transform: 'scale(1)',
                },
                '50%': {
                  opacity: 0.85,
                  transform: 'scale(1.1)',
                },
              },
            }}
          />
        </Fade>
      </Box>
    </Fade>
  );

  if (fullScreen) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: theme.palette.background.default,
          display: 'flex',
          flexDirection: 'column',
          zIndex: 9999,
        }}
      >
        {/* Logo centered */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {loaderContent}
        </Box>
        
        {/* Progress bar with message at bottom center - 60% width */}
        {displayMessage && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '60%',
              maxWidth: 800,
              p: 3,
              backgroundColor: theme.palette.background.default,
            }}
          >
            <Fade in={true} key={displayMessage} timeout={500}>
              <Box>
                <Box sx={{ width: '100%', mb: 1.5 }}>
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 3,
                        backgroundColor: theme.palette.primary.main,
                      },
                    }}
                  />
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: 14,
                    fontWeight: 400,
                    letterSpacing: '0.01em',
                    textAlign: 'center',
                  }}
                >
                  {displayMessage}
                </Typography>
              </Box>
            </Fade>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 200,
        width: '100%',
      }}
    >
      {loaderContent}
    </Box>
  );
};

Loader.propTypes = {
  message: PropTypes.string,
  sequentialMessages: PropTypes.arrayOf(PropTypes.string),
  messageDelay: PropTypes.number,
  fullScreen: PropTypes.bool,
};

export default Loader;
